// A run of ESV verses in the scripture column. Every block on the page comes
// from the same cached chapter query — one call to the ESV API per chapter,
// however many rows the commentary is cut into — and is narrowed here to the
// verses of its row.
//
// With a parallel translation chosen, the same range is set beside it in
// public-domain text. That column is plain: no `.v` spans, so a highlight
// keyed to the ESV's wording can never be painted onto another translation's.
import { bookById, toDisplay, type PassageView, type Ref, type TranslationId } from "@bibleunleashed/shared";
import { useQuery } from "@tanstack/react-query";
import { useLayoutEffect, useRef, useState } from "react";
import { markTerms } from "@/glossary/markTerms";
import { api } from "@/lib/api";
import { useTermMatcher } from "@/lib/glossary";
import { useSettings } from "@/lib/settings";
import { useChapterText } from "@/lib/study";
import { scriptureClass } from "@/reader/scriptureClass";
import { sliceVerses, tidyMarkers, wrapVerses } from "@/reader/verses";

export function usePassage(osis: string | undefined) {
  return useQuery({
    queryKey: ["passage", osis ?? ""],
    queryFn: () => api<PassageView>(`/api/passage/${encodeURIComponent(osis!)}`),
    staleTime: 24 * 3_600_000,
    gcTime: 24 * 3_600_000,
    enabled: Boolean(osis),
  });
}

/** "Nehemiah 1:1–3", or "Nehemiah 1:4" for a single verse. */
export function rangeDisplay(book: string, chapter: number, start: number, end: number): string {
  const ref: Ref = { book, chapter, verse: start };
  if (end > start) {
    ref.endChapter = chapter;
    ref.endVerse = end;
  }
  return toDisplay(ref);
}

export function Passage({
  osis,
  book,
  chapter,
  startVerse,
  endVerse,
  showLabel = false,
  notedVerses,
  focusRef,
  parallel,
}: {
  osis: string;
  book: string;
  chapter: number;
  startVerse: number;
  endVerse: number;
  /** Off by default: on a chapter page the section header names the range. */
  showLabel?: boolean;
  /** Verses the signed-in member has written a note on, marked under the words. */
  notedVerses?: readonly string[];
  /** The verse the study rail is about, marked so it is obvious which one that is. */
  focusRef?: string | null;
  /** A public-domain translation to set beside the ESV. */
  parallel?: TranslationId;
}) {
  const { settings } = useSettings();
  const body = useRef<HTMLDivElement>(null);
  const [empty, setEmpty] = useState(false);
  const { data, isLoading, error } = usePassage(osis);
  const terms = useTermMatcher();

  useLayoutEffect(() => {
    const el = body.current;
    if (!el || !data) return;
    el.innerHTML = data.html;
    wrapVerses(el, osis);
    tidyMarkers(el);
    sliceVerses(el, startVerse, endVerse);
    markTerms(el, terms);
    setEmpty(!el.textContent?.trim());
  }, [data, osis, startVerse, endVerse, terms]);

  // Applied here rather than by the page, because this is the effect that
  // rewrites the markup: a class the page put on afterwards would vanish the
  // next time the passage was rebuilt. `data` and `terms` are in the
  // dependencies so this re-runs whenever that happens. (Highlights cannot work
  // this way — the CSS highlight registry is global, so the page paints those
  // once for all rows.)
  useLayoutEffect(() => {
    const el = body.current;
    if (!el || !data) return;
    for (const span of el.querySelectorAll<HTMLElement>(".v[data-ref]")) {
      span.classList.toggle("noted", Boolean(span.dataset.ref && notedVerses?.includes(span.dataset.ref)));
      span.classList.toggle("studied", span.dataset.ref === focusRef);
    }
  }, [data, notedVerses, focusRef, startVerse, endVerse, terms]);

  // A heading is ESV editorial text with no counterpart in the parallel
  // corpus, which stores plain verses only. Left in, it pushes the ESV's
  // verse 1 below the other column's — so it's hidden whenever a parallel
  // translation is shown, the same way settings.headings hides it for a
  // reader who has turned headings off.
  const scripture = (
    <div
      ref={body}
      className={parallel ? `${scriptureClass(settings)} hide-headings` : scriptureClass(settings)}
      style={{ "--size-step": settings.size } as React.CSSProperties}
    />
  );

  return (
    <div className="scripture-block" id={`v${startVerse}`}>
      {showLabel && (
        <p className="mb-2 font-sans text-[11px] tracking-[0.08em] text-ink-3 uppercase">
          {rangeDisplay(book, chapter, startVerse, endVerse)}
        </p>
      )}
      {isLoading && <p className="font-serif text-ink-3">Loading the passage…</p>}
      {error && <p className="font-serif text-[14px] text-ink-2">Couldn’t load the passage: {error.message}</p>}
      {empty && !isLoading && !error && (
        <p className="font-serif text-[14px] text-ink-2">
          {bookById(book)?.name ?? book} {chapter} has no {endVerse > startVerse ? "such verses" : "such verse"} — check
          this section’s range.
        </p>
      )}
      {/* The same shape with or without a translation beside it. The scripture
          div is filled imperatively, and React reconciles by type and position:
          moving it between a bare child and a cell of the grid had React reuse
          one layout's div for the other and leave the text it had already
          painted sitting above the new one. */}
      <div className={parallel ? "grid gap-6 sm:grid-cols-2 sm:gap-8" : undefined}>
        <div className="min-w-0">
          {parallel && (
            <p className="mb-1 font-sans text-[10.5px] tracking-[0.08em] text-ink-3 uppercase">ESV</p>
          )}
          {scripture}
        </div>
        {parallel && (
          <ParallelColumn translation={parallel} osis={osis} startVerse={startVerse} endVerse={endVerse} />
        )}
      </div>
    </div>
  );
}

/**
 * The same verses in a public-domain translation. Rendered as text rather than
 * as the reader's `.v` spans on purpose: highlights, notes and the context menu
 * are anchored to the ESV's wording, and a second copy of `data-ref="Neh.1.3"`
 * on the page would have them colouring the wrong words.
 */
function ParallelColumn({
  translation,
  osis,
  startVerse,
  endVerse,
}: {
  translation: TranslationId;
  osis: string;
  startVerse: number;
  endVerse: number;
}) {
  const { settings } = useSettings();
  const { data, isLoading } = useChapterText(translation, osis);
  const verses = (data?.verses ?? []).filter((v) => v.verse >= startVerse && v.verse <= endVerse);

  return (
    <div className="min-w-0">
      <p className="mb-1 font-sans text-[10.5px] tracking-[0.08em] text-ink-3 uppercase">{translation}</p>
      <div
        className={`${scriptureClass(settings, "parallel")} text-ink-2`}
        style={{ "--size-step": settings.size } as React.CSSProperties}
      >
        {isLoading && <p className="text-ink-3">…</p>}
        {!isLoading && !verses.length && <p className="text-[14px] text-ink-3">Not loaded for this chapter.</p>}
        {/* One flowing paragraph, not a line per verse: these texts carry no
            paragraph marks of their own, and a column of stacked lines beside
            the ESV's prose reads as a concordance rather than as scripture. */}
        {verses.length > 0 && (
          <p>
            {verses.map((v) => (
              <span key={v.verse}>
                <b className="verse-num">{v.verse}</b> {v.text}{' '}
              </span>
            ))}
          </p>
        )}
      </div>
    </div>
  );
}
